import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/auth';
import { supabase } from '../lib/supabase';
import { useActivityTracking } from './useActivityTracking';

export interface BookmarkedPost {
  id: string;
  post_id: string;
  user_id: string;
  created_at: string;
  // Post details
  post: {
    id: string;
    caption: string;
    media_url: string;
    media_type: 'image' | 'video' | 'audio';
    created_at: string;
    user_id: string;
    likes: number;
    views: number;
    users: {
      id: string;
      username: string;
      avatar_url: string;
      full_name: string;
    };
  };
}

export function useBookmarks() {
  const { user } = useAuth();
  const { trackPostInteraction } = useActivityTracking();
  const [bookmarkedPosts, setBookmarkedPosts] = useState<BookmarkedPost[]>([]);
  const [bookmarkedProfiles, setBookmarkedProfiles] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load bookmarked posts
  const loadBookmarkedPosts = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      const { data: postBookmarks, error: fetchError } = await supabase
        .from('bookmarks')
        .select(`
          id,
          post_id,
          user_id,
          created_at,
          post:posts(
            id,
            caption,
            media_url,
            media_type,
            created_at,
            user_id,
            likes,
            views,
            users:users!id(
              id,
              username,
              avatar_url,
              full_name
            )
          )
        `)
        .eq('user_id', user.id)
        .not('post_id', 'is', null);

      if (fetchError) throw fetchError;

      if (postBookmarks) {
        const transformedPosts = postBookmarks.map((bookmark: any) => ({
          ...bookmark,
          post: {
            ...bookmark.post[0], // Take first element from array
            users: bookmark.post[0]?.users[0] || {} // Take first element from users array
          }
        }));
        setBookmarkedPosts(transformedPosts);
      }
    } catch (err) {
      console.error('Error loading bookmarked posts:', err);
      setError('Failed to load bookmarked posts');
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Load bookmarked profiles
  const loadBookmarkedProfiles = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      // Profile bookmarks not supported in current database schema
      // The bookmarks table doesn't have a profile_id column
      console.log('Profile bookmarks not supported in current database schema');
      setBookmarkedProfiles([]);
    } catch (err) {
      console.error('Error loading bookmarked profiles:', err);
      setError('Failed to load bookmarked profiles');
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Check if a post is bookmarked
  const isPostBookmarked = useCallback(async (postId: string): Promise<boolean> => {
    if (!user) return false;

    try {
      const { data, error } = await supabase
        .from('bookmarks')
        .select('id')
        .eq('user_id', user.id)
        .eq('post_id', postId)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return !!data;
    } catch (err) {
      console.error('Error checking post bookmark:', err);
      return false;
    }
  }, [user]);

  // Check if a profile is bookmarked
  const isProfileBookmarked = useCallback(async (_profileId: string): Promise<boolean> => {
    if (!user) return false;

    try {
      // Profile bookmarks not supported in current database schema
      // The bookmarks table doesn't have a profile_id column
      console.log('Profile bookmarks not supported in current database schema');
      return false;
    } catch (err) {
      console.error('Error checking profile bookmark:', err);
      return false;
    }
  }, [user]);

  // Bookmark a post
  const bookmarkPost = useCallback(async (postId: string, postData?: any) => {
    if (!user) throw new Error('User not authenticated');

    try {
      const { error } = await supabase
        .from('bookmarks')
        .insert([{
          user_id: user.id,
          post_id: postId
        }]);

      if (error) throw error;

      // Track the bookmark activity
      if (postData) {
        await trackPostInteraction(
          postId,
          'bookmark',
          'Added bookmark',
          postData.caption || '',
          postData.username || ''
        );
      }

      // Reload bookmarked posts
      await loadBookmarkedPosts();
    } catch (err) {
      console.error('Error bookmarking post:', err);
      throw err;
    }
  }, [user, trackPostInteraction, loadBookmarkedPosts]);

  // Unbookmark a post
  const unbookmarkPost = useCallback(async (postId: string, postData?: any) => {
    if (!user) throw new Error('User not authenticated');

    try {
      const { error } = await supabase
        .from('bookmarks')
        .delete()
        .eq('user_id', user.id)
        .eq('post_id', postId);

      if (error) throw error;

      // Track the unbookmark activity
      if (postData) {
        await trackPostInteraction(
          postId,
          'bookmark',
          'Removed bookmark',
          postData.caption || '',
          postData.username || ''
        );
      }

      // Reload bookmarked posts
      await loadBookmarkedPosts();
    } catch (err) {
      console.error('Error unbookmarking post:', err);
      throw err;
    }
  }, [user, trackPostInteraction, loadBookmarkedPosts]);

  // Bookmark a profile
  const bookmarkProfile = useCallback(async (profileId: string) => {
    if (!user) throw new Error('User not authenticated');

    try {
      const { error } = await supabase
        .from('bookmarks')
        .insert([{
          user_id: user.id,
          profile_id: profileId
        }]);

      if (error) throw error;

      // Reload bookmarked profiles
      await loadBookmarkedProfiles();
    } catch (err) {
      console.error('Error bookmarking profile:', err);
      throw err;
    }
  }, [user, loadBookmarkedProfiles]);

  // Unbookmark a profile
  const unbookmarkProfile = useCallback(async (profileId: string) => {
    if (!user) throw new Error('User not authenticated');

    try {
      const { error } = await supabase
        .from('bookmarks')
        .delete()
        .eq('user_id', user.id)
        .eq('profile_id', profileId);

      if (error) throw error;

      // Reload bookmarked profiles
      await loadBookmarkedProfiles();
    } catch (err) {
      console.error('Error unbookmarking profile:', err);
      throw err;
    }
  }, [user, loadBookmarkedProfiles]);

  // Load data on mount
  useEffect(() => {
    if (user) {
      loadBookmarkedPosts();
      loadBookmarkedProfiles();
    }
  }, [user, loadBookmarkedPosts, loadBookmarkedProfiles]);

  return {
    bookmarkedPosts,
    bookmarkedProfiles,
    loading,
    error,
    loadBookmarkedPosts,
    loadBookmarkedProfiles,
    isPostBookmarked,
    isProfileBookmarked,
    bookmarkPost,
    unbookmarkPost,
    bookmarkProfile,
    unbookmarkProfile
  };
}
